import type { PackedLandmarks } from '../tracking/types'
import { frameWidthAtDepth, unprojectAtDepth, type CameraModel } from './model'
import type { Vec3 } from '../math/vec3'
import type { Mat4 } from '../math/mat4'
import { CANONICAL_FACE_POSITIONS, CANONICAL_FACE_VERTEX_COUNT } from '../data/canonical-face'

export interface LandmarkDepthModel {
  /** Depth (cm, positive) of the landmark z = 0 plane. */
  depthRef: number
  /** Multiplier converting landmark z (× frame width at depthRef) into cm. */
  zScale: number
}

/**
 * Lift normalized image landmarks to approximate metric camera-space points (cm).
 * Each landmark stays exactly on its own pixel ray (so 2-D alignment is perfect);
 * depth = depthRef + z_norm * frameWidthCm * zScale (MediaPipe's z is in image-width units,
 * negative = closer to the camera). Use `fitLandmarkDepth` to calibrate `depthRef`/`zScale`
 * against the head matrix so the lifted mesh is consistent with matrix-anchored content.
 */
export function landmarksToMetric(
  out: Float32Array,
  landmarks: PackedLandmarks,
  cam: CameraModel,
  depth: LandmarkDepthModel | number,
): Float32Array {
  const depthRef = typeof depth === 'number' ? depth : depth.depthRef
  const zScale = typeof depth === 'number' ? 1 : depth.zScale
  const n = (landmarks.length / 3) | 0
  const widthCm = frameWidthAtDepth(cam, depthRef)
  const p: Vec3 = [0, 0, 0]
  for (let i = 0; i < n; i++) {
    const nx = landmarks[i * 3]!
    const ny = landmarks[i * 3 + 1]!
    const nz = landmarks[i * 3 + 2]!
    unprojectAtDepth(p, cam, nx, ny, depthRef + nz * widthCm * zScale)
    out[i * 3] = p[0]
    out[i * 3 + 1] = p[1]
    out[i * 3 + 2] = p[2]
  }
  return out
}

/** Single-landmark version of `landmarksToMetric`. */
export function landmarkToMetric(
  out: Vec3,
  landmarks: PackedLandmarks,
  index: number,
  cam: CameraModel,
  depth: LandmarkDepthModel | number,
): Vec3 {
  const depthRef = typeof depth === 'number' ? depth : depth.depthRef
  const zScale = typeof depth === 'number' ? 1 : depth.zScale
  const widthCm = frameWidthAtDepth(cam, depthRef)
  const d = depthRef + landmarks[index * 3 + 2]! * widthCm * zScale
  return unprojectAtDepth(out, cam, landmarks[index * 3]!, landmarks[index * 3 + 1]!, d)
}

/**
 * Least-squares fit of (depthRef, zScale) so that lifted landmark depths match the depths of the
 * canonical face model transformed by the head matrix:  depth_i ≈ depthRef + zScale·z_i·W.
 * This makes the landmark-lifted occluder depth-consistent with head-matrix-anchored wearables.
 * Falls back to depthRef = −T.z, zScale = 1 on degenerate input.
 */
export function fitLandmarkDepth(
  landmarks: PackedLandmarks,
  headMatrix: Mat4,
  cam: CameraModel,
  out: LandmarkDepthModel = { depthRef: 0, zScale: 1 },
): LandmarkDepthModel {
  const n = Math.min(CANONICAL_FACE_VERTEX_COUNT, (landmarks.length / 3) | 0)
  const tz = -headMatrix[14]!
  const W = frameWidthAtDepth(cam, tz)
  // matrix row for z: z_cam = m2*x + m6*y + m10*z + m14
  const m2 = headMatrix[2]!, m6 = headMatrix[6]!, m10 = headMatrix[10]!, m14 = headMatrix[14]!
  let sx = 0, sy = 0, sxx = 0, sxy = 0
  for (let i = 0; i < n; i++) {
    const cx = CANONICAL_FACE_POSITIONS[i * 3]!, cy = CANONICAL_FACE_POSITIONS[i * 3 + 1]!, cz = CANONICAL_FACE_POSITIONS[i * 3 + 2]!
    const target = -(m2 * cx + m6 * cy + m10 * cz + m14) // positive depth
    const x = landmarks[i * 3 + 2]! * W
    sx += x
    sy += target
    sxx += x * x
    sxy += x * target
  }
  const denom = n * sxx - sx * sx
  if (n < 8 || Math.abs(denom) < 1e-9) {
    out.depthRef = tz
    out.zScale = 1
    return out
  }
  const b = (n * sxy - sx * sy) / denom
  const a = (sy - b * sx) / n
  out.zScale = Number.isFinite(b) && b > 0.2 && b < 5 ? b : 1
  out.depthRef = Number.isFinite(a) ? a : tz
  return out
}
